import { useFormikContext } from 'formik';
import constants from 'jsx/common/constants';
import { Grid } from '@mss/mss-component-library';
import React, { useContext, useEffect, useState } from 'react';
import {
  AssignedUser,
  AttributeInput,
  EntityInput,
  FromInput,
  FunctionSelector,
  MessageInput,
  MssIdSelector,
  NestedSelectorProps,
  OperatorSelector,
  PrioritySelect,
  ServiceNowPayloadInput,
  ServicenowConfigSelectInput,
  SimpleOperatorSelector,
  TimeForInput,
  TitleInput,
  SubjectInput,
  ReceiverInput,
  ToInput,
  ValueInput,
  EmailInputTags,
  TriggerUseDefaultEvent,
  ServiceNowDescriptionInput,
} from './Inputs';
import { AutomationsContext } from './AutomationForm';

export const ConditionEntityValue = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  const showAssetSelector =
    ![
      constants.automation.types.trigger.TIME,
      constants.automation.types.trigger.TIME_PATTERN,
    ].includes(values.trigger.type) || props.type === 'trigger';
  return (
    <>
      {showAssetSelector && (
        <Grid.Col width={6}>
          <MssIdSelector required {...props} />
        </Grid.Col>
      )}
      <Grid.Col width={6}>
        <OperatorSelector required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <EntityInput required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <ValueInput required {...props} />
      </Grid.Col>
    </>
  );
};

export const ActionServiceServicenow = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();

  return (
    <>
      <Grid.Col width={12}>
        <TitleInput required {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <MssIdSelector
          {...props}
          isTimePattern={values.trigger.type === 'TIME_PATTERN'}
          isTime={values.trigger.type === 'TIME'}
          isAction
          required
        />
      </Grid.Col>
      <Grid.Col width={12}>
        <ServiceNowDescriptionInput required {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <ServicenowConfigSelectInput required {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <ServiceNowPayloadInput {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <EmailInputTags {...props} />
      </Grid.Col>
    </>
  );
};
export const ActionServiceAlert = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();

  const showAssetSelector =
    ![
      constants.automation.types.trigger.TIME,
      constants.automation.types.trigger.TIME_PATTERN,
    ].includes(values.trigger.type) || props.type === 'trigger';

  return (
    <>
      <Grid.Col width={6}>
        <PrioritySelect required {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <TitleInput required {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <AssignedUser required canInherit={showAssetSelector} {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <MssIdSelector
          {...props}
          isTimePattern={values.trigger.type === 'TIME_PATTERN'}
          isTime={values.trigger.type === 'TIME'}
          isAction
          required
        />
      </Grid.Col>
      <Grid.Col width={12}>
        <MessageInput required {...props} />
      </Grid.Col>
      <Grid.Col width={12}>
        <EmailInputTags {...props} />
      </Grid.Col>
    </>
  );
};

export const ActionSendEmail = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  const [isReceiverVisible, setIsReceiverVisible] = useState(false);
  const showAssetSelector =
    ![
      constants.automation.types.trigger.TIME,
      constants.automation.types.trigger.TIME_PATTERN,
    ].includes(values.trigger.type) || props.type === 'trigger';
  return (
    <>
      <Grid.Col width={12}>
        <SubjectInput {...props} required />
      </Grid.Col>
      <Grid.Col width={12}>
        <MessageInput {...props} required />
      </Grid.Col>
      {(isReceiverVisible ||
        values?.actions[props?.index || '']?.settings
          ?.send_custodian_email) && (
        <Grid.Col width={12}>
          <ReceiverInput canInherit={showAssetSelector} {...props} />
        </Grid.Col>
      )}
      <Grid.Col width={12}>
        <EmailInputTags
          {...props}
          sendToCustodians={check => setIsReceiverVisible(check)}
        />
      </Grid.Col>
    </>
  );
};

export const ActionServiceForm = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  const { onSelectedServiceChange } = props;
  const service: any = values.actions[props.index || 0].settings.service;
  const forms: any = {
    'alert.create': () => <ActionServiceAlert {...props} />,
    'alert.servicenow': () => (
      <ActionServiceServicenow subtype="incident" {...props} />
    ),
    'alert.servicenow.case': () => (
      <ActionServiceServicenow subtype="case" {...props} />
    ),
    email: () => <ActionSendEmail {...props} />,
  };

  useEffect(() => {
    if (onSelectedServiceChange) {
      onSelectedServiceChange(service);
    }
  }, [service, onSelectedServiceChange]);

  if (!service) return <></>;

  if (!forms[service])
    return <pre className="danger">Unsupported service type {service}</pre>;
  return forms[service]();
};

export const AutomationEventRange = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  const showAssetSelector =
    ![
      constants.automation.types.trigger.TIME,
      constants.automation.types.trigger.TIME_PATTERN,
    ].includes(values.trigger.type) || props.type === 'trigger';

  return (
    <>
      {showAssetSelector && (
        <Grid.Col width={6}>
          <MssIdSelector required {...props} />
        </Grid.Col>
      )}
      <Grid.Col width={6}>
        <FunctionSelector required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <OperatorSelector required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <EntityInput required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <ValueInput required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <TimeForInput valueKey="time_period_seconds" required {...props} />
      </Grid.Col>
    </>
  );
};

export const TriggerTime = (props: NestedSelectorProps) => {
  return (
    <>
      {props.type === 'condition' && (
        <Grid.Col width={6}>
          <OperatorSelector required {...props} />
        </Grid.Col>
      )}
      <Grid.Col width={6}>
        <TimeForInput valueKey="time" required {...props} />
      </Grid.Col>
    </>
  );
};

export const TriggerTimePattern = (props: NestedSelectorProps) => {
  return (
    <>
      <Grid.Col width={6}>
        <TimeForInput required {...props} />
      </Grid.Col>
    </>
  );
};

export const AutomationAttribute = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  // const showAssetSelector =
  //   ![
  //     constants.automation.types.trigger.TIME,
  //     constants.automation.types.trigger.TIME_PATTERN,
  //   ].includes(values.trigger.type) || props.type === 'trigger';
  const { triggerAttributes } = useContext(AutomationsContext);
  const attributesOptions = triggerAttributes
    ? triggerAttributes[values.trigger.type]
    : null;

  return (
    <>
      {/* {showAssetSelector && (
        <Grid.Col width={6}>
          <MssIdSelector {...props} />
        </Grid.Col>
      )} */}
      <Grid.Col width={6}>
        <AttributeInput
          required
          {...props}
          attributesOptions={attributesOptions}
        />
      </Grid.Col>
      <Grid.Col width={6}>
        <SimpleOperatorSelector required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <ValueInput required {...props} />
      </Grid.Col>
    </>
  );
};

export const AutomationStateChangeOperator = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  const showAssetSelector =
    ![
      constants.automation.types.trigger.TIME,
      constants.automation.types.trigger.TIME_PATTERN,
    ].includes(values.trigger.type) || props.type === 'trigger';

  return (
    <>
      {showAssetSelector && (
        <Grid.Col width={6}>
          <MssIdSelector required {...props} />
        </Grid.Col>
      )}
      <Grid.Col width={6}>
        <OperatorSelector required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <EntityInput required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <ValueInput required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <TriggerUseDefaultEvent {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <TimeForInput required valueKey="time" {...props} />
      </Grid.Col>
    </>
  );
};

export const AutomationStateChange = (props: NestedSelectorProps) => {
  const { values } = useFormikContext<any>();
  const showAssetSelector =
    ![
      constants.automation.types.trigger.TIME,
      constants.automation.types.trigger.TIME_PATTERN,
    ].includes(values.trigger.type) || props.type === 'trigger';

  return (
    <>
      {showAssetSelector && (
        <Grid.Col width={6}>
          <MssIdSelector required {...props} />
        </Grid.Col>
      )}
      <Grid.Col width={6}>
        <FromInput {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <EntityInput required {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <ToInput {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <TriggerUseDefaultEvent {...props} />
      </Grid.Col>
      <Grid.Col width={6}>
        <TimeForInput required valueKey="time" {...props} />
      </Grid.Col>
    </>
  );
};
